import { useCallback, useEffect, useRef, useState } from 'react';
import { type Id, toast } from 'react-toastify';
import {
  useCreateAndStartBowtieCreationJobMutation,
  useGetBackgroundJobDetailsQuery,
} from '../api/enhanced/enhanced-v4-api';
import { useDeleteBowtieAndMoveFiltersMutation } from '../api/generated/bowtie-api';
import type { BowtieDto } from '../api/generated/v4-api';
import { useDiagramContext } from '../context/diagram.context';
import { updateToast } from '../helpers/toast-util';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { type DiagramDisplayFilterState, selectFilter, setDiagramDisabled } from '../redux/slices/diagram';
import { JobStatus, setJobStatus, submitJob } from '../redux/slices/running-jobs';

/**
 * Custom hook for creating backend records from a Bowtie diagram.
 * Handles the creation process, status monitoring, and UI feedback.
 *
 * @param bowtieDto - Optional BowtieDto for flow diagram
 * @param mueLabel - Optional Material Unwanted Event label
 * @param mueRecordId - Optional Material Unwanted Event record ID
 *
 * @returns Object containing:
 * - handleClick: Function to trigger the backend records creation
 * - disabled: Boolean indicating if the creation process is in progress
 *
 * @remarks
 * This hook manages the entire lifecycle of creating backend records:
 * - Initiates the creation job
 * - Monitors job status through polling
 * - Handles success/failure scenarios
 * - Manages toast notifications
 * - Updates URL parameters on success
 * - Cleans up saved diagrams if necessary
 *
 * @example
 * ```typescript
 * const { handleClick, disabled } = useCreateBackendRecords(bowtieDto);
 * ```
 */
const useCreateBackendRecords = (bowtieDto?: BowtieDto, mueLabel?: string, mueRecordId?: number) => {
  const [jobId, setJobId] = useState<number>();

  const toastIdRef = useRef<Id | undefined>(undefined);
  const dispatch = useAppDispatch();
  const { diagramId } = useDiagramContext();
  const diagramFilterData = useAppSelector(selectFilter);

  const [postCreateRecordsJob] = useCreateAndStartBowtieCreationJobMutation();
  const [deleteBowtieAndMoveFilters] = useDeleteBowtieAndMoveFiltersMutation();

  const { data: { status, jobDetails } = {}, isError } = useGetBackgroundJobDetailsQuery(
    { id: jobId! },
    {
      skip: !jobId,
      pollingInterval: jobId ? 5000 : 0, // 5 seconds polling interval
    }
  );

  useEffect(() => {
    if ('FINISHED' === status && jobDetails && toastIdRef.current) {
      dispatch(setDiagramDisabled(false));
      setJobId(undefined);

      const parsedJobDetails = JSON.parse(jobDetails);
      if (parsedJobDetails?.recordId) {
        const recordId = String(parsedJobDetails.recordId);
        const params = new URLSearchParams({ records: recordId });

        cleanupDiagramAndFilters(recordId, diagramId, diagramFilterData)
          .then(() => {
            updateToast(toastIdRef.current!, 'Diagram created successfully', 'success');
          })
          .catch(() => {
            //unable to recover diagram filters
            updateToast(
              toastIdRef.current!,
              'Diagram created successfully, though filters could not be persisted',
              'success'
            );
          })
          .finally(() => {
            dispatch(
              setJobStatus({
                id: jobId!,
                status: JobStatus.Success,
                url: `${window.location.origin}${window.location.pathname}?${params}`,
                completedDate: new Date().toISOString(),
              })
            );

            setTimeout(
              () => window.location.replace(`${window.location.origin}${window.location.pathname}?${params}`),
              2500
            );
          });
      }
    } else if (('STAGED' === status || isError) && toastIdRef.current) {
      updateToast(toastIdRef.current, 'Unable to create diagram', 'error');

      dispatch(
        setJobStatus({
          id: jobId!,
          status: JobStatus.Failed,
          completedDate: new Date().toISOString(),
        })
      );

      dispatch(setDiagramDisabled(false));

      setJobId(undefined);
    }
  }, [status, isError]);

  const cleanupDiagramAndFilters = useCallback(
    async (recordId: string, diagramId: string | null, diagramFilterData?: DiagramDisplayFilterState) => {
      if (diagramId) {
        return deleteBowtieAndMoveFilters({
          id: diagramId,
          recordId,
          diagramFilterDto: { displayFilter: { ...(diagramFilterData ?? {}) } },
        }).unwrap();
      } else {
        return Promise.reject('Unable to cleanup diagram and filters - missing diagram ID');
      }
    },
    []
  );

  const handleClick = useCallback(async () => {
    if (!mueLabel && !mueRecordId) {
      toast.error('Please specify the Material Unwanted Event', { autoClose: 2000 });
      return;
    }

    toastIdRef.current = toast.loading('Creating backend records...');

    // post records creation job
    const payload = { bowtieDto: bowtieDto! };

    try {
      const { id } = (await postCreateRecordsJob(payload).unwrap()) ?? {};

      setJobId(id);
      if (id) {
        dispatch(submitJob({ name: bowtieDto?.riskScenario, id: id! }));

        dispatch(setDiagramDisabled(true));
      }
    } catch {
      updateToast(toastIdRef.current, 'Unable to create diagram', 'error');
    }
  }, [mueLabel, mueRecordId, bowtieDto]);

  return {
    handleClick,
    disabled: Boolean(jobId),
  };
};

export default useCreateBackendRecords;
